// Жесты секций на обзорной волне веб-плеера (оба экрана — телефон и широкий).
//
//   - тап / клик по секции        → onSeek(section.startSec, id) — РОВНО одна перемотка
//   - второй тап в пределах 320 мс → onToggleLoop(id, loopBefore) — без второй перемотки
//   - клик мимо секций            → onBackgroundClick(ev) — точная перемотка в точку
//   - Escape                      → onToggleLoop('', getLoop()) — снять петлю (текущую);
//                                   если Escape уже принят другим (defaultPrevented) — молчим
//
// Почему счётчик тапов, а не dblclick / долгое нажатие (решение Сергея 16.10,
// B-261012-web-mobile-touch-controls-hard): iOS Safari не присылает dblclick на
// двойной ТАП, а 500 мс долгого нажатия были единственным способом поставить
// петлю на телефоне. Один счётчик даёт один жест: двойной клик на компьютере,
// двойной тап на телефоне.
//
// loopBefore — петля ДО первого тапа пары. Первый тап может перенести петлю на
// новую секцию (waves.ts), и без этого двойной тап по уже зацикленной секции
// не смог бы её снять.
//
// Элементы с data-wave-control (метка петли, метка зума) пропускаются: у их
// кнопок свои обработчики. Слушатель делегирован на host — переживает
// перерисовку секций.

import type { ShareManifest } from '@bandway/share-types';
import { createTapTracker } from '../song-screen/gestures.js';

type Section = ShareManifest['sections'][number];

export interface SectionHandlersOpts {
  onSeek: (timeSec: number, sectionId: string) => void;
  onToggleLoop: (sectionId: string, loopBefore: string | null) => void;
  onBackgroundClick?: (ev: MouseEvent) => void;
  /** Текущая петля (id секции) — чтобы передать её состояние до пары тапов. */
  getLoop?: () => string | null;
  now?: () => number;
}

export interface SectionHandlersHandle { unmount(): void; }

const FLASH_MS = 210;
const DOUBLE_TAP_MS = 320;
const DOUBLE_TAP_SLOP_PX = 24;

export function mountSectionHandlers(
  host: HTMLElement,
  sections: Section[],
  opts: SectionHandlersOpts,
): SectionHandlersHandle {
  const byId = new Map(sections.map((s) => [s.id, s]));
  const taps = createTapTracker(DOUBLE_TAP_MS, DOUBLE_TAP_SLOP_PX);
  const now = opts.now ?? ((): number => performance.now());
  const getLoop = opts.getLoop ?? ((): string | null => null);
  let loopBefore: string | null = null;

  function flashSection(target: HTMLElement): void {
    target.classList.add('is-flash');
    setTimeout(() => target.classList.remove('is-flash'), FLASH_MS);
  }

  function handleClick(ev: Event): void {
    const e = ev as MouseEvent;
    const el = e.target as HTMLElement | null;
    if (el?.closest('[data-wave-control]')) return;
    const target = el?.closest<HTMLElement>('[data-section-id]');
    if (!target) { taps.reset(); opts.onBackgroundClick?.(e); return; }
    const sec = byId.get(target.dataset.sectionId!);
    if (!sec) return;
    if (taps.tap(sec.id, e.clientX, e.clientY, now()) === 'double') {
      opts.onToggleLoop(sec.id, loopBefore);
      return;
    }
    loopBefore = getLoop();
    opts.onSeek(sec.startSec, sec.id);
    flashSection(target);
  }

  function handleKey(ev: KeyboardEvent): void {
    // Escape, который уже принял кто-то другой (лист действий, чарт), петлю не снимает:
    // один Escape — одно дело. Принявший вызывает preventDefault.
    if (ev.defaultPrevented) return;
    if (ev.key === 'Escape') opts.onToggleLoop('', getLoop());
  }

  host.addEventListener('click', handleClick);
  document.addEventListener('keydown', handleKey);

  return {
    unmount() {
      host.removeEventListener('click', handleClick);
      document.removeEventListener('keydown', handleKey);
    },
  };
}
